import { useMemo } from "react"

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Minus, Plus } from "lucide-react"
import { DateTime } from "luxon"

import { Button } from "@/components/ui/button.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx"
import type { KPI } from "@/types/kpiTypes.ts"
import { numberFmt } from "@/util/numberFmt.ts"

import { getSubRows } from "../util/getSubRows.ts"

const columnHelper = createColumnHelper<KPI>()

export default function KPIBrowserTable({
  dates,
  paddingArray,
  data,
  isLoading,
  rows,
}: {
  dates: string[]
  paddingArray: number[]
  data: KPI[]
  isLoading: boolean
  rows: number
}) {
  const columnDefs = useMemo(() => {
    const nameCol = [
      columnHelper.accessor((kpi) => kpi.id, {
        id: "names",
        header: () => <div className="py-2 px-4 w-[300px]">KPIs</div>,
        cell: ({ row }) => (
          <div
            className="font-semibold"
            style={{
              paddingLeft: `${
                row.depth == 0
                  ? paddingArray[row.index]
                  : paddingArray[row.getParentRow()?.index || 0]
              }px`,
            }}
          >
            {row.depth == 0 ? (
              <>
                {row.original.name} <i>({row.original.unit})</i>{" "}
              </>
            ) : (
              <>{row.original.name}</>
            )}
          </div>
        ),
      }),
      columnHelper.display({
        id: "expand",
        header: () => <>Show Budget</>,
        cell: ({ row }) => (
          <div className="text-center">
            <Button
              variant="ghost"
              disabled={!row.getCanExpand()}
              onClick={row.getToggleExpandedHandler()}
            >
              {row.getIsExpanded() ? <Minus /> : <Plus />}
            </Button>
          </div>
        ),
      }),
    ]
    const cols = dates.map((date) =>
      columnHelper.accessor(
        (kpi) => {
          if (kpi.values?.findIndex((v) => v.type == "Actual") == -1) {
            return kpi.values?.find((v) => v.date == date)?.value
          } else {
            return kpi.values?.find((v) => v.date == date && v.type == "Actual")?.value
          }
        },
        {
          id: date,
          header: () => <>{DateTime.fromISO(date).toFormat("L/d/yy")}</>,
          cell: (context) => (
            <div>
              {typeof context.getValue() != "number" ? (
                <span className="text-gray-300 italic">N/A</span>
              ) : (
                numberFmt(Number(context.getValue()), context.row.original.unit == "$")
              )}
            </div>
          ),
        },
      ),
    )
    return [...nameCol, ...cols]
  }, [dates, paddingArray])

  const table = useReactTable<KPI>({
    data,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (kpi) => getSubRows(kpi),
  })

  return (
    <Table>
      {table.getHeaderGroups().map((hg, i) => (
        <TableHeader className="bg-card-header sticky top-0" key={i}>
          <TableRow>
            {hg.headers.map((header, i) => (
              <TableHead key={i} className="py-2">
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
      ))}
      <TableBody>
        {isLoading
          ? [...Array(rows).keys()].map((i) => (
              <TableRow key={i}>
                <TableCell colSpan={table.getAllColumns().length} className="h-[52.5px]">
                  <Skeleton className="w-full h-full" />
                </TableCell>
              </TableRow>
            ))
          : table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={row.depth == 0 ? "text-foreground" : "text-muted-foreground"}
              >
                {row.getVisibleCells().map((cell, i) => (
                  <TableCell key={i}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
      </TableBody>
    </Table>
  )
}
